'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { motion, useTransform, type MotionValue } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { customRichTextTags } from '@/utils/i18n';
import { PatientCards } from './reanalysis-patient-card';
import FeaturedPatient from './reanalysis-featured-patient';
import ReanalysisSummary from './reanalysis-summary';
import DesktopSectionEclipse from './desktop-section-eclipse';
import {
  CONCLUSION_SETTLED,
  LINK_VISIBLE,
  REANALYSIS_SCENES,
  SCENE_START,
  SCENE_STARTS,
} from './reanalysis-timeline';

// 1920×1080 고정 캔버스를 축소해 그리므로 뷰포트별 줄바꿈 분기가 없다 — 항상 줄바꿈하는 <br>
// question 은 모바일(reanalysis-mobile)과 문구를 공유하되 끊는 지점이 달라, <mobileBr>은 여기서 무시한다
const richTags = { br: customRichTextTags.solidBr, mobileBr: () => null };

export default function AnimatedReanalysis({
  progress,
}: {
  progress: MotionValue<number>;
}) {
  const t = useTranslations('Main.reanalysis');
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [scene, setScene] = useState(0);
  const [showLink, setShowLink] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () =>
      setScale(
        Math.min(element.clientWidth / 1920, element.clientHeight / 1080),
      );
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, []);
  // progress 는 부모(ScrollStage)의 렌더 도중에도 갱신되므로, 곧바로 상태를 바꾸면
  // 다른 컴포넌트를 렌더하는 중에 setState 하게 된다. 프레임당 한 번만 장면을 맞춘다.
  useEffect(() => {
    let frame: number | undefined;
    const apply = () => {
      frame = undefined;
      const value = progress.get();
      setScene(
        Math.max(
          0,
          SCENE_STARTS.findLastIndex(start => value >= start),
        ),
      );
      setShowLink(value >= LINK_VISIBLE);
    };
    const schedule = () => {
      if (frame === undefined) frame = requestAnimationFrame(apply);
    };
    apply();
    const unsubscribe = progress.on('change', schedule);
    return () => {
      unsubscribe();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [progress]);
  const backgroundOpacity = useTransform(progress, [0.04, 0.12], [0, 1]);
  const backgroundWash = useTransform(progress, [0.25, 0.4], [0, 0.4]);
  const questionOpacity = useTransform(
    progress,
    [0.24, SCENE_START.focus],
    [1, 0],
  );
  const darkOpacity = useTransform(
    progress,
    [SCENE_START.conclusion, CONCLUSION_SETTLED],
    [0, 1],
  );
  return (
    <div
      ref={ref}
      data-reanalysis-scene={REANALYSIS_SCENES[scene]}
      className="relative isolate h-[var(--scroll-stage-height)] bg-[#0d0e10] [clip-path:inset(calc(-1*var(--scroll-stage-top))_0_0)]"
    >
      <div className="absolute inset-x-0 bottom-0 top-[calc(-1*var(--scroll-stage-top))] overflow-hidden bg-[#0d0e10]">
        <motion.div
          className="absolute inset-0 bg-white"
          style={{ opacity: backgroundOpacity }}
        >
          <Image
            src="/images/main/reanalysis/background.webp"
            alt=""
            fill
            sizes="100vw"
            className="scale-[1.105] object-cover"
          />
          <motion.div
            className="absolute inset-0 bg-white"
            style={{ opacity: backgroundWash }}
          />
        </motion.div>
      </div>
      <motion.h2
        className="absolute inset-0 flex items-center justify-center text-center text-[48px] font-semibold leading-[56px] tracking-[-1.072px] text-white"
        style={{ opacity: questionOpacity }}
      >
        {t.rich('question', richTags)}
      </motion.h2>
      <motion.div
        className="absolute inset-x-0 bottom-0 top-[calc(-1*var(--scroll-stage-top))] bg-[#0d0e10]"
        style={{ opacity: darkOpacity }}
      />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div
            className="relative h-[1080px] w-[1920px]"
            style={{ transform: `scale(${scale})` }}
          >
            <PatientCards progress={progress} />
            <FeaturedPatient progress={progress} />
            <ReanalysisSummary
              progress={progress}
              visible={scene >= REANALYSIS_SCENES.indexOf('summary')}
              showLink={showLink}
            />
          </div>
        </div>
      </div>
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{ opacity: darkOpacity }}
        aria-hidden="true"
      >
        <DesktopSectionEclipse half="upper" />
      </motion.div>
    </div>
  );
}
